import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Drives a print run for the org chart.
 *
 * Three of the things a print needs are NOT achievable in a print stylesheet,
 * because they are React state or inline styles rather than CSS the print rules
 * can override:
 *
 *  1. A collapsed subtree is not in the DOM at all (the view components render
 *     no children for a collapsed node), so no CSS can reveal it. The scope has
 *     to be expanded in state first.
 *  2. The zoom is an inline `transform: scale()` and the wrapper's width/height
 *     are inline pixel values. Inline styles beat a print stylesheet on
 *     specificity, so zoom has to be reset in state.
 *  3. The tree-versus-list decision depends on a measured width, which is only
 *     known at runtime.
 *
 * So this hook applies an override, waits for React to paint it, calls
 * window.print(), then restores what it changed. The print dialog is
 * synchronous and blocking in every browser that matters, so the restore after
 * it returns is safe: the snapshot has already been taken by then.
 *
 * @param {object} opts
 * @param {() => object} opts.snapshot captures the state to restore
 * @param {(o: object) => void} opts.apply applies the print override
 * @param {(s: object) => void} opts.restore puts the captured state back
 */
export default function useOrgChartPrint({ snapshot, apply, restore }) {
  // The override the page should render while a print is pending, or null when
  // no print is in flight. Held in state (not a ref) precisely because the page
  // has to RE-RENDER with it applied before the dialog opens.
  const [pending, setPending] = useState(null);
  const savedRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Snapshot and apply happen together, synchronously, in the same event
  // handler. Applying in a separate effect keyed on `pending` would work only
  // as long as that effect kept running before the print effect below, which is
  // an ordering guarantee not worth resting print correctness on. Doing it here
  // means the override is already in state before the print effect ever sees a
  // non-null `pending`.
  const start = useCallback((override) => {
    savedRef.current = snapshot();
    apply(override);
    setPending(override);
  }, [snapshot, apply]);

  // Runs AFTER the browser has painted the override (useEffect, not
  // useLayoutEffect: a layout effect fires before paint, so the dialog would
  // capture the pre-override frame and print the unexpanded, still-zoomed tree).
  //
  // The double requestAnimationFrame is not superstition: the first callback
  // runs before the frame the effect scheduled has been painted, the second
  // runs after it. Chrome and Safari both snapshot for print from the last
  // painted frame, so printing from the first callback races the paint.
  useEffect(() => {
    if (!pending) return undefined;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        try {
          if (typeof window !== 'undefined' && typeof window.print === 'function') {
            window.print();
          }
        } finally {
          // Restore even if print() threw or the reader cancelled the dialog:
          // leaving the page stuck in its print override (fully expanded, zoom
          // 1, scoped to one department) would look like the export had
          // corrupted the view.
          if (mountedRef.current) {
            if (savedRef.current) restore(savedRef.current);
            savedRef.current = null;
            setPending(null);
          }
        }
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
    // `restore` is intentionally read fresh on each run via the parent's
    // useCallback identity; `pending` is the trigger.
  }, [pending, restore]);

  return { printing: !!pending, pending, start };
}
